import {
    Badge,
    Card,
    CardContent,
    CardLoadMore,
    Cell,
    Col,
    Column,
    DataTable,
    Icon,
    Input,
    NavButton,
    Page,
    PageHeader,
    PageNav,
    Row,
    Text,
    Highlighter
} from "@tv2no/ui-react";
import dayjs from "dayjs";
import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    getCountyList,
    getMunicipalityList,
    getZipCodeList,
    updateCounty,
    updateMunicipality,
    updateZipCode
} from "../../api/services/geo-lists";
import { useDebounce } from "../../utils/hooks";
import { columnLabels, sortColumns } from "./helpers";
import { EditGeoEntry } from "./parts/EditGeoEntry";

const geoLists = {
    counties: {
        label: "Fylker",
        identField: "id",
        fetchList: getCountyList,
        updateEntry: updateCounty
    },
    municipalities: {
        label: "Kommuner",
        identField: "id",
        fetchList: getMunicipalityList,
        updateEntry: updateMunicipality
    },
    zip_codes: {
        label: "Postnummer",
        identField: "zip",
        fetchList: getZipCodeList,
        updateEntry: updateZipCode
    }
};

export const GeoLists = () => {
    const navigate = useNavigate();
    const { unitKey } = useParams();
    const [listData, setListData] = useState(null);
    const [pageNumber, setPageNumber] = useState(0);
    const [editEntry, setEditEntry] = useState(null);
    const [busyLoading, setBusyLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState(null);
    const [reloadKey, setReloadKey] = useState(null);

    useDebounce(
        () => {
            if (unitKey) {
                const unit = geoLists[unitKey];
                setBusyLoading(true);
                unit.fetchList({ page: pageNumber, size: 50, search: searchTerm }).then(
                    (response) => {
                        setListData((list) => {
                            if (response?.number > 0) {
                                return {
                                    ...response,
                                    content: list.content.concat(response.content)
                                };
                            }
                            return response;
                        });
                        setBusyLoading(false);
                    }
                );
            } else {
                navigate(`/geo/counties`);
            }
        },
        [unitKey, pageNumber, reloadKey, navigate, searchTerm],
        500,
        false
    );

    return (
        <Page>
            <PageHeader icon="tools" title="Geo-lister" subtitle="Skattelister" />
            <PageNav>
                {Object.keys(geoLists).map((key, idx) => (
                    <NavButton
                        key={key}
                        icon="arrow-right"
                        label={geoLists[key].label}
                        active={unitKey === key}
                        onClick={() => setPageNumber(0) & navigate(`/geo/${key}`)}
                    />
                ))}
            </PageNav>
            <Card>
                <CardContent border="bottom">
                    <Row>
                        <Col lg={10} xs={20}>
                            <Input
                                icon="search"
                                placeholder="Søk"
                                value={searchTerm}
                                onChange={setSearchTerm}
                            />
                        </Col>
                        <Col lg={14} xs={4} align={["right", "center"]}>
                            {busyLoading ? (
                                <Icon name="refresh" rotate />
                            ) : (
                                <Badge>{listData?.totalElements}</Badge>
                            )}
                        </Col>
                    </Row>
                </CardContent>
                <CardContent>
                    <DataTable busy={!listData} rows={listData?.content} onRowClick={setEditEntry}>
                        {Object.keys(listData?.content?.[0] || {})
                            ?.sort(sortColumns)
                            .map((field) => (
                                <Column
                                    key={field}
                                    label={columnLabels[field] || field}
                                    cell={({ row }) => (
                                        <CellView value={row[field]} searchTerm={searchTerm} />
                                    )}
                                />
                            ))}
                    </DataTable>
                    {listData?.content?.length === 0 ? (
                        <Text opacity={50}>Ingen funnet</Text>
                    ) : null}
                </CardContent>
                {!listData?.last && (
                    <CardLoadMore
                        busy={busyLoading}
                        onClick={() => setPageNumber((num) => num + 1)}
                        progress={((listData?.number + 1) / listData?.totalPages) * 100}
                    />
                )}
            </Card>

            {editEntry && (
                <EditGeoEntry
                    unit={geoLists[unitKey]}
                    entry={editEntry}
                    onClose={() => setEditEntry(null)}
                    onReload={() => setPageNumber(0) & setReloadKey(Math.random())}
                />
            )}
        </Page>
    );
};

const CellView = ({ value, searchTerm }) => {
    return (
        <Cell>
            {value === true ? (
                <Icon name="check" color="success" />
            ) : value === false ? (
                <Icon name="close" color="error" />
            ) : Array.isArray(value) ? (
                value.map((val) => (
                    <Badge fill color="silent" spacing="right" key={val}>
                        {val}
                    </Badge>
                ))
            ) : typeof value === "object" ? (
                value?.name
            ) : !isNaN(value) && value.toString().length >= 13 ? (
                dayjs(value).format("L")
            ) : (
                <Highlighter term={searchTerm}>{value}</Highlighter>
            )}
        </Cell>
    );
};
